import React from 'react';
import { styled } from '@glitz/react';
import ProductFeatureViewModel from 'Product/ProductFeature/ProductFeatureViewModel.type';
import ProductFeature from 'Product/ProductFeature';
import { AspectImageVideoBackground } from 'Shared/SharedComponents/molecules/ImageVideoBackground';
import {
  H2,
  Paragraph,
  ResponsiveSize,
  ResponsiveLineHeight,
} from 'Shared/SharedComponents/atoms/Typography';
import { Preset } from 'Shared/Image';
import { calculateTopPadding, Ratio } from 'Shared/AspectRatio';
import SwiperList from 'Shared/SwiperList';
import { createGridClasses, createOffsetGridClassLarge } from 'Shared/grid';
import * as style from 'Shared/Style';

type PropType = {
  features: ProductFeatureViewModel[];
  smallLayout: boolean;
  isCompact: boolean;
  skipGridOffSet?: boolean;
  label: string;
};

const BigContentWrap = styled.div({
  position: 'relative',
  height: '100%',
  justifyItems: 'center',
  alignContent: 'center',
})

const BigIconWrap = styled.div({
  position: 'absolute',
  textAlign: 'center',
  width: '100%',
  marginTop: '104px',
})

const BigParagraphWrap = styled.div({
  position: 'absolute',
  bottom: '40px',
  width: '100%'
})

const BigParagraph = styled(Paragraph, {
  textAlign: 'center',
})

const CenterContent = styled.div({
  textAlign: 'center',
})

const SwiperWrap = styled.div({
  position: 'relative',
  color: style.colors.monochrome.white,
})

const Image = styled.img({
  width: '106px',
  height: '106px',
  ['@media ' + style.mediaMinLarge]: {
    width: '95px',
    height: '95px',
  },
})

export const ProductFeatureList = (props: PropType) => {
  const { features, smallLayout, isCompact, label } = props;
  const totalCount = features?.length ?? 0;
  if (smallLayout)
    return (
      <div className='grid'>
        <div className={createGridClasses(12, !props.skipGridOffSet ? 11 : 12, !props.skipGridOffSet ? createOffsetGridClassLarge(2) : '')}>
          <div>
            <SwiperList
              slidesPerView={isCompact ? 1.2 : 3.6666}
              slidesPerGroup={isCompact ? 1 : 2}
              spaceBetween={isCompact ? 8 : 16}
              isCompact={isCompact}
              showNav={!isCompact && totalCount > 3}
              freeMode={false}
              cssMode={true}
              parallax={false}
              alternativeNav={true}
              listHeading={label}
              showSwirl={isCompact && totalCount > 1}
            >
              {features?.map((feature, index) => (
                <ProductFeature className="swiper-slide" key={index} feature={feature} />
              ))}
            </SwiperList>
          </div>
        </div>
      </div>
    );

  return (
    <SwiperWrap>
      <SwiperList
        slidesPerView={1}
        spaceBetween={0}
        isCompact={isCompact}
        showNav={false}
        pagination
        freeMode={false}
        paginationContainerCss={{
          position: 'absolute',
          bottom: '172px',
          left: `calc(50% - ${((totalCount - 1) * 4) + ((totalCount - 1) * 6) + 'px'} + 12px)`,
          zIndex: 1,
        }}
        pagingItemCount={features?.length}
        pagingIconColor={style.colors.monochrome.white}
        cssMode={true}
      >
        {features?.map((feature, index) => (
          <div key={index} className='swiper-slide'>
            <AspectImageVideoBackground
              paddingTop={
                isCompact
                  ? `${calculateTopPadding(Ratio.TwelveToTwentyThree)}`
                  : `${calculateTopPadding(Ratio.SixteenToNine)}`
              }
              preset={Preset.FullWidth}
              image={feature.backgroundImage}
              videoSources={feature.backgroundVideoSources}
              breakPoint={isCompact ? 0 : 1}
            >
              <BigContentWrap className='grid' key={index}>
                <BigIconWrap className={createGridClasses(12, 0)}>
                  <CenterContent>
                    <Image src={feature.icon?.url} />
                  </CenterContent>
                </BigIconWrap>
                <CenterContent className={createGridClasses(12, 8, createOffsetGridClassLarge(3))}>
                  <H2>{feature.heading}</H2>
                </CenterContent>
                <BigParagraphWrap>
                  <div className='grid'>
                    <div className={createGridClasses(12, 4, createOffsetGridClassLarge(5))}>
                      <BigParagraph
                        fontSize={ResponsiveSize.D16_M16}
                        lineHeight={ResponsiveLineHeight.D130_M130}
                      >
                        {feature.text}
                      </BigParagraph>
                    </div>
                  </div>
                </BigParagraphWrap>
              </BigContentWrap>
            </AspectImageVideoBackground>
          </div>
        ))}
      </SwiperList>
    </SwiperWrap>
  );
};

export default ProductFeatureList;
